'use client'

import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { ShoppingCart, TrendingUp, Clock, Shield, DollarSign, FileCheck } from 'lucide-react'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
}

const item = {
  hidden: { opacity: 0, scale: 0.95 },
  show: { opacity: 1, scale: 1 }
}

const advantageIcons = {
  buy: [Clock, TrendingUp, DollarSign, FileCheck, Shield],
  sell: [DollarSign, TrendingUp, Clock, Shield, FileCheck]
}

export const SeoContent = () => {
  const t = useTranslations('seoContent')

  const advantages = [
    {
      type: 'buy',
      icon: ShoppingCart,
      title: t('buyAdvantages.title'),
      items: [
        { icon: advantageIcons.buy[0], text: t('buyAdvantages.item1') },
        { icon: advantageIcons.buy[1], text: t('buyAdvantages.item2') },
        { icon: advantageIcons.buy[2], text: t('buyAdvantages.item3') },
        { icon: advantageIcons.buy[3], text: t('buyAdvantages.item4') },
        { icon: advantageIcons.buy[4], text: t('buyAdvantages.item5') }
      ]
    },
    {
      type: 'sell',
      icon: TrendingUp,
      title: t('sellAdvantages.title'),
      items: [
        { icon: advantageIcons.sell[0], text: t('sellAdvantages.item1') },
        { icon: advantageIcons.sell[1], text: t('sellAdvantages.item2') },
        { icon: advantageIcons.sell[2], text: t('sellAdvantages.item3') },
        { icon: advantageIcons.sell[3], text: t('sellAdvantages.item4') },
        { icon: advantageIcons.sell[4], text: t('sellAdvantages.item5') }
      ]
    }
  ]

  return (
    <div className="w-full">
      <section className="container py-12 md:py-24">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-12"
        >
          {t('title')}
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mx-auto max-w-4xl mb-8 md:mb-12"
        >
          <div className="space-y-6 text-muted-foreground text-center">
            <p className="text-base md:text-lg leading-relaxed">{t('description1')}</p>

            <div className="space-y-4">
              <p className="text-base md:text-lg leading-relaxed">
                <strong className="text-foreground">{t('description2')}</strong>
              </p>
              <p className="text-base md:text-lg leading-relaxed">
                <strong className="text-foreground">{t('description3')}</strong>
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6 md:gap-8"
        >
          {advantages.map((advantage, index) => {
            const MainIcon = advantage.icon
            return (
              <motion.div
                variants={item}
                transition={{ duration: 0.5 }}
                key={index}
                className="p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                    <MainIcon className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold">{advantage.title}</h3>
                </div>

                <div className="space-y-3">
                  {advantage.items.map((item, itemIndex) => {
                    const ItemIcon = item.icon
                    return (
                      <div key={itemIndex} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-50 dark:bg-orange-900/10 flex items-center justify-center flex-shrink-0">
                          <ItemIcon className="w-4 h-4 text-orange-500" />
                        </div>
                        <span className="text-sm md:text-base text-muted-foreground">
                          {item.text}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </motion.div>
            )
          })}
        </motion.div>
      </section>
    </div>
  )
}
